import React, { useEffect } from "react";
import { string, object } from "yup";
import {
  Box,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";

import { useFormik } from "formik";

export default function SurveyForm({ updateDetailsCC, error, loading }) {
  const formik = useFormik({
    initialValues: {
      awareCC: "",
      seeCC: "",
      useCC: "",
    },
    validationSchema: object().shape({
      awareCC: string().required("Required"),
      seeCC: string().required("Required"),
      useCC: string().required("Required"),
    }),
  });

  useEffect(() => {
    updateDetailsCC(formik.values);
  }, [formik.values]);

  return (
    <Box
      sx={{
        my: { xl: 5, xs: 5, md: 1 },
        width: "100%",
      }}
    >
      <Typography>{loading ? "Loading..." : ""}</Typography>
      <Typography sx={{ color: "red", fontWeight: "bold" }}>
        {error}
        {error ? " (Unable to display questions)" : ""}
      </Typography>
      {!error && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            textAlign: "left",
            fontWeight: "bold",
          }}
        >
          <FormControl component="fieldset" sx={{ mb: 2, mt: 2 }}>
            <Typography
              sx={{
                color: "black",
                fontSize: {
                  xs: "0.875rem",
                  sm: "1rem",
                  md: "0.8rem",
                  xl: "1rem",
                },
                mb: 2,
              }}
            >{`1. Are you aware of the Citizen's Charter - document of the SDO/School
              services and requirements?`}</Typography>
            <RadioGroup
              aria-label="awareCC"
              name="awareCC"
              value={formik.values.awareCC}
              onChange={(evt) =>
                formik.setFieldValue("awareCC", evt.target.value)
              }
              onBlur={(e) => {
                formik.handleBlur(e);
              }}
              error={formik.touched.awareCC && Boolean(formik.errors.awareCC)}
              helperText={formik.touched.awareCC && formik.errors.awareCC}
              sx={{
                ml: 2,
                // gap: 1, // Ensures spacing between options
                display: "flex",
                flexDirection: {
                  xs: "column",
                  sm: "row",
                  md: "column",
                  xl: "column",
                }, // Column on mobile, row on larger screens
                alignItems: {
                  xs: "flex-start",
                  sm: "center",
                  md: "flex-start",
                  xl: "flex-start",
                }, // Align options properly
                flexWrap: "wrap", // Prevents overflow
                "@media (min-height: 1080px)": {
                  flexDirection: "column",
                  alignItems: "flex-start",
                },
              }}
              row
            >
              <FormControlLabel
                value="Yes"
                control={
                  <Radio
                    sx={{
                      transform: { xs: "scale(0.7)", sm: "scale(1.2)" }, // Slightly larger on small screens
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontSize: {
                        xs: "0.875rem",
                        sm: "1rem",
                        md: "0.8rem",
                        xl: "1rem",
                      }, // Responsive text size
                      fontWeight: 500, // Slightly bold for readability
                      whiteSpace: "nowrap", // Prevents text from wrapping awkwardly
                      overflow: "hidden", // Ensures clean UI
                      textOverflow: "ellipsis", // Adds "..." if text is too long
                    }}
                  >
                    Yes
                  </Typography>
                }
              />
              <FormControlLabel
                value="No"
                control={
                  <Radio
                    sx={{
                      transform: { xs: "scale(0.7)", sm: "scale(1.2)" }, // Slightly larger on small screens
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontSize: {
                        xs: "0.875rem",
                        sm: "1rem",
                        md: "0.8rem",
                        xl: "1rem",
                      }, // Responsive text size
                      fontWeight: 500, // Slightly bold for readability
                      whiteSpace: "nowrap", // Prevents text from wrapping awkwardly
                      overflow: "hidden", // Ensures clean UI
                      textOverflow: "ellipsis", // Adds "..." if text is too long
                    }}
                  >
                    No
                  </Typography>
                }
              />
            </RadioGroup>
          </FormControl>
          <FormControl component="fieldset" sx={{ mb: 2, mt: 2 }}>
            <Typography
              sx={{
                color: "black",
                fontSize: {
                  xs: "0.875rem",
                  sm: "1rem",
                  md: "0.8rem",
                  xl: "1rem",
                },
                mb: 2,
              }}
            >{`2. Did you see the SDO/School Citizen's Charter (online or posted in the office)?`}</Typography>
            <RadioGroup
              aria-label="seeCC"
              name="seeCC"
              value={formik.values.seeCC}
              onChange={(evt) =>
                formik.setFieldValue("seeCC", evt.target.value)
              }
              onBlur={(e) => {
                formik.handleBlur(e);
              }}
              error={formik.touched.seeCC && Boolean(formik.errors.seeCC)}
              helperText={formik.touched.seeCC && formik.errors.seeCC}
              sx={{
                ml: 2,
                // gap: 1, // Ensures spacing between options
                display: "flex",
                flexDirection: {
                  xs: "column",
                  sm: "row",
                  md: "column",
                  xl: "column",
                }, // Column on mobile, row on larger screens
                alignItems: {
                  xs: "flex-start",
                  sm: "center",
                  md: "flex-start",
                  xl: "flex-start",
                }, // Align options properly
                flexWrap: "wrap", // Prevents overflow
                "@media (min-height: 1080px)": {
                  flexDirection: "column",
                  alignItems: "flex-start",
                },
              }}
              row
            >
              <FormControlLabel
                value="Yes - It was easy to find"
                control={
                  <Radio
                    sx={{
                      transform: { xs: "scale(0.7)", sm: "scale(1.2)" }, // Slightly larger on small screens
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontSize: {
                        xs: "0.875rem",
                        sm: "1rem",
                        md: "0.8rem",
                        xl: "1rem",
                      }, // Responsive text size
                      fontWeight: 500, // Slightly bold for readability
                      whiteSpace: "nowrap", // Prevents text from wrapping awkwardly
                      overflow: "hidden", // Ensures clean UI
                      textOverflow: "ellipsis", // Adds "..." if text is too long
                    }}
                  >
                    Yes - It was easy to find
                  </Typography>
                }
              />
              <FormControlLabel
                value="Yes - It was hard to find"
                control={
                  <Radio
                    sx={{
                      transform: { xs: "scale(0.7)", sm: "scale(1.2)" }, // Slightly larger on small screens
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontSize: {
                        xs: "0.875rem",
                        sm: "1rem",
                        md: "0.8rem",
                        xl: "1rem",
                      }, // Responsive text size
                      fontWeight: 500, // Slightly bold for readability
                      whiteSpace: "nowrap", // Prevents text from wrapping awkwardly
                      overflow: "hidden", // Ensures clean UI
                      textOverflow: "ellipsis", // Adds "..." if text is too long
                    }}
                  >
                    Yes - It was hard to find
                  </Typography>
                }
              />
              <FormControlLabel
                value="No"
                control={
                  <Radio
                    sx={{
                      transform: { xs: "scale(0.7)", sm: "scale(1.2)" }, // Slightly larger on small screens
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontSize: {
                        xs: "0.875rem",
                        sm: "1rem",
                        md: "0.8rem",
                        xl: "1rem",
                      }, // Responsive text size
                      fontWeight: 500, // Slightly bold for readability
                      whiteSpace: "nowrap", // Prevents text from wrapping awkwardly
                      overflow: "hidden", // Ensures clean UI
                      textOverflow: "ellipsis", // Adds "..." if text is too long
                    }}
                  >
                    No
                  </Typography>
                }
              />
            </RadioGroup>
          </FormControl>
          <FormControl component="fieldset" sx={{ mb: 2, mt: 2 }}>
            <Typography
              sx={{
                color: "black",
                fontSize: {
                  xs: "0.875rem",
                  sm: "1rem",
                  md: "0.8rem",
                  xl: "1rem",
                },
                mb: 2,
              }}
            >{`3. Did you use the SDO/School Citizen's Charter as a guide for the service you availed?`}</Typography>
            <RadioGroup
              aria-label="useCC"
              name="useCC"
              value={formik.values.useCC}
              onChange={(evt) =>
                formik.setFieldValue("useCC", evt.target.value)
              }
              onBlur={(e) => {
                formik.handleBlur(e);
              }}
              error={formik.touched.useCC && Boolean(formik.errors.useCC)}
              helperText={formik.touched.useCC && formik.errors.useCC}
              sx={{
                ml: 2,
                // gap: 1, // Ensures spacing between options
                display: "flex",
                flexDirection: {
                  xs: "column",
                  sm: "row",
                  md: "column",
                  xl: "column",
                }, // Column on mobile, row on larger screens
                alignItems: {
                  xs: "flex-start",
                  sm: "center",
                  md: "flex-start",
                  xl: "flex-start",
                }, // Align options properly
                flexWrap: "wrap", // Prevents overflow
                "@media (min-height: 1080px)": {
                  flexDirection: "column",
                  alignItems: "flex-start",
                },
              }}
              row
            >
              <FormControlLabel
                value="Yes"
                control={
                  <Radio
                    sx={{
                      transform: { xs: "scale(0.7)", sm: "scale(1.2)" }, // Slightly larger on small screens
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontSize: {
                        xs: "0.875rem",
                        sm: "1rem",
                        md: "0.8rem",
                        xl: "1rem",
                      }, // Responsive text size
                      fontWeight: 500, // Slightly bold for readability
                      whiteSpace: "nowrap", // Prevents text from wrapping awkwardly
                      overflow: "hidden", // Ensures clean UI
                      textOverflow: "ellipsis", // Adds "..." if text is too long
                    }}
                  >
                    Yes
                  </Typography>
                }
              />
              <FormControlLabel
                value="No"
                control={
                  <Radio
                    sx={{
                      transform: { xs: "scale(0.7)", sm: "scale(1.2)" }, // Slightly larger on small screens
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontSize: {
                        xs: "0.875rem",
                        sm: "1rem",
                        md: "0.8rem",
                        xl: "1rem",
                      }, // Responsive text size
                      fontWeight: 500, // Slightly bold for readability
                      whiteSpace: "nowrap", // Prevents text from wrapping awkwardly
                      overflow: "hidden", // Ensures clean UI
                      textOverflow: "ellipsis", // Adds "..." if text is too long
                    }}
                  >
                    No
                  </Typography>
                }
              />
            </RadioGroup>
          </FormControl>
        </Box>
      )}
    </Box>
  );
}
